'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { type ReactNode, useSyncExternalStore } from 'react';
import DocumentNavigation from '@/app/[locale]/(worlds)/[roomId]/components/DocumentNavigation';
import { needsDocumentNavigation } from '@/lib/room-isolation';
const subscribe = () => () => {};
const getDocumentLoadUrl = () => {
  const [entry] = performance.getEntriesByType('navigation');
  return entry ? new URL(entry.name) : null;
};
type RoomIsolationGateProps = {
  /** Whether a document load of the room being rendered is cross-origin isolated (`isRoomCrossOriginIsolated`). */
  isTargetRoomCrossOriginIsolated: boolean;
  children: ReactNode;
};

/**
 * Renders the room only when this document's isolation (`crossOriginIsolated`) matches the room's;
 * a room reached by a client navigation that does not match gets a full document navigation instead
 * (CONNECT-6329). The server render and hydration of a document load always render the room.
 */
export default function RoomIsolationGate(t0) {
  const $ = _c(9);
  const {
    isTargetRoomCrossOriginIsolated,
    children
  } = t0;
  const pathname = usePathname();
  const t1 = useSearchParams();
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toString();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const query = t2;
  const href = query ? `${pathname}?${query}` : pathname;
  let t3;
  if ($[2] !== href || $[3] !== isTargetRoomCrossOriginIsolated) {
    t3 = () => needsDocumentNavigation({
      documentLoadUrl: getDocumentLoadUrl(),
      isDocumentCrossOriginIsolated: self.crossOriginIsolated,
      targetRoomUrl: new URL(href, location.origin),
      isTargetRoomCrossOriginIsolated,
      isFramed: self !== top
    });
    $[2] = href;
    $[3] = isTargetRoomCrossOriginIsolated;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const shouldNavigate = useSyncExternalStore(subscribe, t3, _temp);
  let t4;
  if ($[5] !== children || $[6] !== href || $[7] !== shouldNavigate) {
    t4 = shouldNavigate ? <DocumentNavigation href={href} /> : children;
    $[5] = children;
    $[6] = href;
    $[7] = shouldNavigate;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp() {
  return false;
}